<template>
  <div id="restart-btn">
    <div
      class="click"
      id="abandon-roadbook-open"
      @click="noConfirm ? restart() : open()"
    >
      <slot name="openButton">
        <div
          id="abandon-roadbook-open-border"
          :class="{ small }"
        >
          <rc-svg
            name="croix"
            id="abandon-roadbook-svg"
            :class="{ small }"
          ></rc-svg>
        </div>
        <div
          id="abandon-roadbook-text"
          :class="{ small }"
        >
          {{ text }}
        </div>
      </slot>
    </div>
    <!-- Leaving not finished Roadbook-->
    <rc-alert
      :show="!isFinished && isLeaving && isOpen"
      :title-text="$t('error.warning', native)"
      :centered-title="true"
      :width="'900px'"
      :description="
        $t(
          isQualif ? 'roadbook_edit.abandon_warning_qualif' : 'roadbook_edit.abandon_warning',
          native,
        )
      "
      :icon-name="'exclamation'"
      :icon-large="true"
      @confirm="deleteRoadbook()"
      @cancel="isOpen = false"
      :footer-cancel-button-text="$t('btn.cancel', native)"
      :footer-confirm-button-text="$t('btn.abandon', native)"
    ></rc-alert>
    <!-- Saving not finished Roadbook-->
    <rc-alert
      :show="!isFinished && !isLeaving && isOpen"
      :title-text="$t('error.warning', native)"
      :centered-title="true"
      :max-width-desc="'700px'"
      :description="
        $t(
          isQualif ? 'roadbook_edit.saving_warning_qualif' : 'roadbook_edit.saving_warning',
          native,
        )
      "
      :icon-name="'exclamation'"
      :icon-large="true"
      @confirm="restart()"
      @cancel="isOpen = false"
      :footer-cancel-button-text="$t('btn.cancel', native)"
      :footer-confirm-button-text="$t('btn.save', native)"
    ></rc-alert>
    <!-- Leaving completed Roadbook-->
    <rc-alert
      :show="isFinished && isLeaving && isOpen"
      :title-text="$t('error.warning', native)"
      :centered-title="true"
      :max-width-desc="'700px'"
      :description="
        $t(
          isQualif
            ? 'roadbook_edit.abandon_confirm_warning_qualif'
            : 'roadbook_edit.abandon_confirm_warning',
          native,
        )
      "
      :icon-name="'exclamation'"
      :icon-large="true"
      @cancel="isOpen = false"
      @confirm="restart()"
      :footer-cancel-button-text="$t('btn.cancel', native)"
      :footer-confirm-button-text="$t('btn.quit', native)"
    ></rc-alert>
    <!-- Saving completed Roadbook-->
    <rc-alert
      :show="isFinished && !isLeaving && isOpen"
      :title-text="$t('error.warning', native)"
      :centered-title="true"
      :max-width-desc="'700px'"
      :description="
        $t(
          isQualif ? 'roadbook_edit.saving_warning_qualif' : 'roadbook_edit.saving_warning',
          native,
        )
      "
      :icon-name="'exclamation'"
      :icon-large="true"
      @cancel="isOpen = false"
      @confirm="restart()"
      :footer-cancel-button-text="$t('btn.cancel', native)"
      :footer-confirm-button-text="$t('btn.save', native)"
    ></rc-alert>
  </div>
</template>

<script setup lang="ts">
  import { ref, computed } from 'vue';
  import RcAlert from '@/components/feedbacks/RcAlert.vue';
  import RcSvg from '@/components/medias/RcSvg.vue';
  import { useRoadbookStore, useTagManagerStore } from '@/store';
  import { useRouter } from 'vue-router';
  import { useMixin } from '@/utils/mixins/useMixin.mixin';

  // General
  const props = defineProps({
    text: {
      type: String,
      default: () => '',
    },
    delete: {
      type: Boolean,
      default: true,
    },
    noConfirm: {
      type: Boolean,
      default: false,
    },
    small: {
      type: Boolean,
      default: false,
    },
  });
  const { native, deleteRoadbook } = useMixin();
  const router = useRouter();
  const emit = defineEmits(['loading']);
  const isOpen = ref(false);
  const deleteMutable = ref(props.delete);

  const roadbookStore = useRoadbookStore();
  const tagManagerStore = useTagManagerStore();

  const isFinished = computed(() => roadbookStore.metadata.is_finished);
  const isQualif = computed(() => roadbookStore.metadata.is_qualif);
  const isLeaving = ref(false);

  // Methods
  const open = () => {
    if (document.querySelector('#abandon-roadbook-text') || deleteMutable.value) {
      isLeaving.value = true;
    }
    isOpen.value = true;
  };

  const restart = async () => {
    emit('loading', true);
    if (isFinished.value) {
      deleteMutable.value = false;
    }
    if (isQualif.value) {
      deleteMutable.value = false;
      roadbookStore.updateMetadata({
        category: 'finished',
        field: null,
        value: true,
      });
    }
    tagManagerStore.sendEvent({
      event: deleteMutable.value ? 'roadbook_abandon' : 'roadbook_finish',
    });
    await roadbookStore.restart();
    router.push('dashboard');
    emit('loading', false);
  };
</script>

<style lang="postcss" scoped>
  #abandon-roadbook-text {
    font-size: 10px;
    margin-top: 5px;
    @apply text-orange-warn;
    @apply font-light;
    text-align: center;
    width: 65px;
    overflow: hidden;
    text-overflow: ellipsis;

    @media screen and (max-height: 640px) {
      #abandon-roadbook-text {
        @apply hidden;
      }
    }
  }

  .modal-buttons {
    display: flex;
    align-items: center;
    margin: 0 auto 2%;
    flex-direction: column-reverse;
    row-gap: 20px;

    .quit-rdbk {
      cursor: pointer;
      transition: color 300ms ease-in-out;

      @media (hover: hover) {
        &:hover {
          color: theme('colors.primary');
        }
      }
    }
  }

  #restart-btn {
    @apply flex items-center flex-col;
  }

  .abandon-wrapper {
    @apply flex flex-col;
  }

  .icon-error {
    @apply text-[80px] mt-[30px] w-auto text-center text-grey-light;
  }

  #abandon-roadbook-open {
    @apply text-[14px] flex items-center justify-center flex-col;

    @media (hover: hover) {
      &:hover {
        #abandon-roadbook-open-border {
          @apply opacity-100 outline-[0.5px] outline outline-orange-warn;
        }
      }
    }

    #abandon-roadbook-open-border {
      @apply border-solid border-[1.5px] border-orange-warn outline-[0.5px] outline outline-transparent h-[26px] w-[26px] opacity-80 flex items-center justify-center rounded-xl;
      @apply transition duration-500 ease-in-out;
    }

    .open-button-wrapper {
      @apply flex items-center;
    }

    #abandon-roadbook-svg {
      @apply fill-orange-warn h-[11px];
    }

    .abandon-wrapper {
      @apply flex flex-col;
    }

    .icon-error {
      @apply text-[80px] mt-[30px] w-auto text-center text-grey-light;
    }

    #abandon-roadbook-dialog {
      @apply text-center text-[18px] z-[5];

      #abandon-title {
        @apply text-primary text-[35px] font-bold mt-[16px] mb-[32px];
      }
    }

    #abandon-roadbook-open-border.small {
      @apply h-[20px] w-[20px];
    }

    #abandon-roadbook-svg.small {
      @apply h-[9px];
    }
    #abandon-roadbook-text.small {
      margin-top: 0;
    }
  }
</style>
